import React from 'react';
import '../../styles/components/AsperaNumberUnitInput.scss';
import { language } from '../../services/LanguageService';
import { NumberInput, Select, SelectItem } from '../Carbon';
import { findBestNumberUnit, getSafeNumberValue, numberKeyEvent, numberTranslations, observableTimeout } from '../../helpers/starterHelpers';
import { SubManager } from '../../classes/SubManager';
/**
 * Get the list of time units for AsperaNumberUnitInput.
 * If Using this units value should be in SECONDS.
 *
 * @param start - Change the list start
 * @param end - Change the list end
 *
 * @returns array of units to pass to AsperaNumberUnitInput
 */
export const timeUnits = (start, end) => {
    const units = [
        {
            name: language.get('numberUnitInput.seconds'),
            multiplier: 1,
        },
        {
            name: language.get('numberUnitInput.minutes'),
            multiplier: 60,
        },
        {
            name: language.get('numberUnitInput.hours'),
            multiplier: 3600,
        },
        {
            name: language.get('numberUnitInput.days'),
            multiplier: 86400,
        },
        {
            name: language.get('numberUnitInput.weeks'),
            multiplier: 604800,
        },
    ];
    return units.slice(start || 0, end);
};
/**
 * Get the list of byte units for AsperaNumberUnitInput.
 * If Using this units value should be in BYTES.
 *
 * @param start - Change the list start
 * @param end - Change the list end
 *
 * @returns array of units to pass to AsperaNumberUnitInput
 */
export const byteUnits = (start, end) => {
    const units = [
        {
            name: language.get('bytes.byteAbb'),
            multiplier: 1,
        },
        {
            name: language.get('bytes.byteKiloAbb'),
            multiplier: 1000,
        },
        {
            name: language.get('bytes.byteMegaAbb'),
            multiplier: 1000000,
        },
        {
            name: language.get('bytes.byteGigaAbb'),
            multiplier: 1000000000,
        },
        {
            name: language.get('bytes.byteTeraAbb'),
            multiplier: 1000000000000,
        },
    ];
    return units.slice(start || 0, end);
};
/**
 * Get the list of byte units (1024 base) for AsperaNumberUnitInput.
 * If Using this units value should be in BYTES (1024).
 *
 * @param start - Change the list start
 * @param end - Change the list end
 *
 * @returns array of units to pass to AsperaNumberUnitInput
 */
export const byteUnits1024 = (start, end) => {
    const units = [
        {
            name: language.get('bytes.byteAbb'),
            multiplier: 1,
        },
        {
            name: language.get('bytes.byteKiloAbb'),
            multiplier: 1024,
        },
        {
            name: language.get('bytes.byteMegaAbb'),
            multiplier: 1048576,
        },
        {
            name: language.get('bytes.byteGigaAbb'),
            multiplier: 1073741824,
        },
        {
            name: language.get('bytes.byteTeraAbb'),
            multiplier: 1099511627776,
        },
    ];
    return units.slice(start || 0, end);
};
/**
 * Get the list of byte units for AsperaNumberUnitInput.
 * If Using this units value should be in BITS PER SECOND.
 *
 * @param start - Change the list start
 * @param end - Change the list end
 *
 * @returns array of units to pass to AsperaNumberUnitInput
 */
export const bpsUnits = (start, end) => {
    const units = [
        {
            name: language.get('bytes.bitPsAbb'),
            multiplier: 1,
        },
        {
            name: language.get('bytes.bitKiloPsAbb'),
            multiplier: 1000,
        },
        {
            name: language.get('bytes.bitMegaPsAbb'),
            multiplier: 1000000,
        },
        {
            name: language.get('bytes.bitGigaPsAbb'),
            multiplier: 1000000000,
        },
        {
            name: language.get('bytes.bitTeraPsAbb'),
            multiplier: 1000000000000,
        },
    ];
    return units.slice(start || 0, end);
};
/**
 * React Component
 *
 * The AsperaNumberUnitInput provides an input field with number data and units.
 * Pre defined units are available as exports from Aspera Starter.
 *
 * @example
 * ```javascript
 * <AsperaNumberUnitInput units={} value={} onChange={() => {}} />
 * ```
 */
export class AsperaNumberUnitInput extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            loading: true,
        };
        this.currentUnitIndex = 0;
        this.subManager = new SubManager();
        this.triggerChange = (value) => {
            const { onChange, units } = this.props;
            const currentItem = units[this.currentUnitIndex];
            if (typeof onChange !== 'function') {
                return;
            }
            if (currentItem && value) {
                onChange(String(Number(value || '0') * currentItem.multiplier));
            }
            else {
                onChange(value);
            }
            this.setState({});
        };
        this.unitOnChange = (event) => {
            this.currentUnitIndex = Number(event.target.value);
            this.triggerChange(String(this.minValue || 1));
        };
        this.numberOnChange = (_event, data) => {
            this.triggerChange(data.value);
        };
    }
    getComputedUnit(value) {
        const { units } = this.props;
        const currentItem = units[this.currentUnitIndex];
        if (currentItem) {
            return String(getSafeNumberValue(value) / currentItem.multiplier);
        }
        return value;
    }
    get value() {
        const { value } = this.props;
        return this.getComputedUnit(value);
    }
    get maxValue() {
        const { max } = this.props;
        return Math.floor(Number(this.getComputedUnit(String(max || Number.MAX_SAFE_INTEGER))));
    }
    get minValue() {
        const { min } = this.props;
        return Math.ceil(Number(this.getComputedUnit(String(min || 0))));
    }
    get numberInputField() {
        const { onKeyUp, helperText, maxLength, id, invalidText, dataAuto, disabled, label, allowEmpty, hideSteppers, step, placeholder, allowAnyText, allowDecimal, onBlur } = this.props;
        return (React.createElement("div", { className: "aspera-number-unit-input--input-field" },
            React.createElement(NumberInput, { iconDescription: language.get('forms.numberInfo'), autoComplete: 'off', onKeyUp: onKeyUp, helperText: helperText, maxLength: maxLength, invalidText: invalidText || language.get('forms.invalidNumber'), id: id ? `${id}-number-input` : undefined, "data-auto": dataAuto, disabled: disabled, value: this.value, label: label, allowEmpty: allowEmpty, hideSteppers: hideSteppers, max: this.maxValue, min: this.minValue, step: step, placeholder: placeholder, translateWithId: numberTranslations, onChange: this.numberOnChange, onPaste: (event) => {
                    event.preventDefault();
                }, onKeyPress: (event) => {
                    numberKeyEvent(event, this.minValue, allowAnyText, allowDecimal);
                }, onBlur: onBlur })));
    }
    get unitSelection() {
        const { id, disabled, units, onBlur, label } = this.props;
        if (!units.length) {
            return null;
        }
        return (React.createElement("div", { className: `aspera-number-unit-input--select-field ${!label ? 'has-no-label' : ''}` },
            React.createElement(Select, { id: id ? `${id}-number-units` : undefined, disabled: units.length < 2 ? true : disabled, onChange: this.unitOnChange, onBlur: onBlur, defaultValue: this.currentUnitIndex, hideLabel: true, label: language.get('numberUnitInput.units') }, units.map((item, index) => {
                return React.createElement(SelectItem, { key: index, value: index, text: item.name, "data-auto": `form-item-select-item-${index}` });
            }))));
    }
    setupInitialData() {
        const { units, value } = this.props;
        this.currentUnitIndex = findBestNumberUnit(value, units).index;
        this.setState({ loading: false });
    }
    componentDidUpdate(previousProps) {
        const { units } = this.props;
        const firstItem = units[0];
        const firstOldItem = previousProps.units[0];
        if (units.length !== previousProps.units.length ||
            (firstItem === null || firstItem === void 0 ? void 0 : firstItem.multiplier) !== (firstOldItem === null || firstOldItem === void 0 ? void 0 : firstOldItem.multiplier)) {
            this.setState({ loading: true });
            this.subManager.setSubscription(observableTimeout().subscribe({
                next: () => {
                    this.setupInitialData();
                },
                error: () => {
                    this.setupInitialData();
                },
            }));
        }
    }
    componentWillUnmount() {
        this.subManager.unsubscribeAll();
    }
    componentDidMount() {
        this.setupInitialData();
    }
    render() {
        const { className, id, style, light } = this.props;
        const { loading } = this.state;
        return (React.createElement("div", { style: style, className: `aspera-number-unit-input ${className ? className : ''} ${light ? 'light-mode' : ''}`, id: id },
            !loading && this.numberInputField,
            !loading && this.unitSelection));
    }
}
